/* eslint-disable max-lines-per-function */
import { ReactNode, useEffect, useRef, useState } from 'react';

import { ComboBoxInput } from './combo-box-input';
import { ComboBoxMenuProps } from './combo-box-menu';
import { ComboBoxProps } from './combo-box.types';
import { FormHelperText, Popover, mergeStyles } from '../..';
import { MenuItem } from '../filter-combo-box/filter-combo-box.types';

interface ComboBoxCoreProps<T extends MenuItem> extends Omit<ComboBoxProps<T>, 'data'> {
  menuElement?: (props: ComboBoxMenuProps<T>) => JSX.Element;
  menuContent?: ReactNode;
  defaultSearchTerm?: string;
  data?: T[];
}

export const ComboBoxCore = <T extends MenuItem>(props: ComboBoxCoreProps<T>) => {
  const {
    data,
    open: initialOpen,
    placeholder = '',
    className,
    size,
    onChange,
    onSearch,
    onFocus,
    onDeselect: onDeselectCallback,
    onBlur,
    renderListItem,
    defaultSelection,
    defaultSearchTerm = '',
    searchTerm: controlledSearchTerm,
    autoFocus,
    bottomOptionsListContent,
    errorProps,
    containerSize = 'large',
    menuElement: ComboBoxMenu,
    menuContent,
  } = props;
  const [searchTerm, setSearchTerm] = useState<string>(controlledSearchTerm ?? defaultSearchTerm);
  const [selectedItems, setSelectedItems] = useState<Map<string, T>>(
    new Map((defaultSelection ?? []).map((i) => [i.value, i]))
  );
  const [open, setOpen] = useState(initialOpen);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => setSearchTerm(controlledSearchTerm ?? ''), [controlledSearchTerm]);
  useEffect(() => setOpen(initialOpen), [initialOpen]);

  useEffect(() => {
    if (ref.current) {
      const observer = new ResizeObserver((entries) => {
        setWidth(entries[0].contentRect.width);
      });
      const obj = ref.current;
      observer.observe(obj);
      return () => observer.unobserve(obj);
    }
  }, []);

  const setItemSelectedState = (item: T, isSelected: boolean) => {
    const newSelection = new Map(selectedItems);
    if (isSelected) {
      newSelection.set(item.value, item);
    } else {
      newSelection.delete(item.value);
    }
    setSelectedItems(newSelection);
    onChange?.([...newSelection.values()]);
  };

  // make sure that any selected values changes are reflected in the UI
  useEffect(() => {
    const newSelection = new Map();
    defaultSelection?.map((item) => newSelection.set(item.value, item));
    setSelectedItems(newSelection);
    // do not invoke the onChange callback here as this effect was a result of client side selection changes
  }, [defaultSelection]);

  const onToggleItem = (item: T) => {
    setItemSelectedState(item, !selectedItems.has(item.value));
  };

  const onDeselect = (item: T): (() => void) => {
    return () => {
      setItemSelectedState(item, false);
      onDeselectCallback?.(item);
    };
  };

  return (
    <>
      <div className={mergeStyles(['combobox', className])} ref={ref}>
        <Popover
          showArrow={false}
          trigger="external"
          autoPlacementOptions={{ allowedPlacements: ['top', 'bottom'] }}
          overlayStyle={{ width }}
          open={open}
          onOpenChange={(open: boolean) => setOpen(open)}
          containerClassName="w-full"
          innerContentClassName=""
          content={
            ComboBoxMenu ? (
              <ComboBoxMenu
                data={data as T[]}
                selectedItems={selectedItems}
                onToggleItem={onToggleItem}
                renderListItem={renderListItem}
                bottomContent={bottomOptionsListContent}
              />
            ) : (
              menuContent
            )
          }
        >
          <ComboBoxInput
            selectedItems={[...selectedItems.values()]}
            size={size}
            placeholder={selectedItems.size === 0 ? placeholder : ''}
            defaultValue={searchTerm}
            value={searchTerm}
            onSearch={(searchTerm: string) => {
              setSearchTerm(searchTerm);
              onSearch?.(searchTerm);
            }}
            onDeselect={onDeselect}
            onFocus={(e) => {
              setOpen(true);
              onFocus?.(e);
            }}
            onBlur={onBlur}
            autoFocus={autoFocus}
            containerSize={containerSize}
          />
        </Popover>
      </div>
      {errorProps && <FormHelperText {...{ ...errorProps, type: 'error', size: errorProps.size ?? containerSize }} />}
    </>
  );
};

export default ComboBoxCore;
